/**
 * Turning a failed `POST /iam/v4/auth/login` into something the form can show.
 *
 * Kept pure and out of the hook so every branch is testable (same pattern as
 * `session-state.utils`). The shape it maps is v4's:
 *
 *   401 {"error":"invalid_username_password",
 *        "error_description":"User name or password invalid","redirect_url":null}
 *
 * Note the failure is reported at the TOP level as `error` / `error_description`.
 * The nested `message` shape is the older v1/SSO one, kept as a fallback.
 */

export type LoginFormField = "email" | "password";

export type LoginErrorMessage = Readonly<{
  field: LoginFormField;
  /** An i18n key, or the server's own text when it is more specific. */
  message: string;
}>;

type LoginErrorBody = {
  error?: string;
  error_description?: string;
  captcha_required?: boolean;
  message?: string | { error_description?: string };
};

type LoginError = {
  response?: { status?: number; data?: LoginErrorBody };
};

export const resolveLoginError = (err: unknown): LoginErrorMessage => {
  const { response } = (err ?? {}) as LoginError;
  const status = response?.status;
  const data = response?.data;

  const responseMessage = data?.message;
  const errorCode = data?.error;
  const serverMessage =
    data?.error_description ??
    (typeof responseMessage === "string"
      ? responseMessage
      : responseMessage?.error_description);

  // Bad credentials — the case that reaches the UI most often, and the one that
  // used to arrive stripped of its response entirely (the 401 interceptor tried
  // to "refresh" a session that did not exist yet and rejected with ITS error).
  // Prefer the app's own copy over the server's English `error_description` so
  // the message stays consistent and localises.
  if (errorCode === "invalid_username_password") {
    return { field: "password", message: "ERROR_INVALID_PASSWORD_EMAIL" };
  }

  // Was previously compared against `status` (an HTTP code), so it could never
  // match — the inactive/unverified signal is an error *code*.
  if (
    errorCode === "user_inactive_or_not_verified" ||
    errorCode === "user_inactive"
  ) {
    return {
      field: "email",
      message: "ERROR_ACCOUNT_INACTIVE_OR_UNVERIFIED",
    };
  }

  // Captcha is configured per tenant in the Blocks portal. When it is on, login
  // cannot succeed until the form collects and sends `captchaCode` (the site key
  // arrives as `captcha_site_key`), so surface the server's own text rather than
  // a misleading credentials error.
  if (errorCode === "captcha_enabled" || data?.captcha_required) {
    return { field: "email", message: serverMessage || "ERROR_LOGIN_FAILED" };
  }

  // No response at all — the request never completed (offline, DNS, CORS). That
  // is emphatically not "wrong password", so it must not read as one.
  if (!response) {
    return { field: "password", message: "ERROR_LOGIN_FAILED" };
  }

  if (status === 400 || status === 422) {
    return {
      field: "email",
      message: serverMessage || "ERROR_INVALID_CREDENTIALS",
    };
  }
  if (status === 403) {
    return {
      field: "email",
      message: serverMessage || "ERROR_ACCOUNT_DEACTIVATED",
    };
  }
  if (status === 401) {
    return {
      field: "password",
      message: serverMessage || "ERROR_INVALID_PASSWORD_EMAIL",
    };
  }
  if (status === 500) {
    return { field: "email", message: "ERROR_SERVER" };
  }

  return { field: "password", message: serverMessage || "ERROR_LOGIN_FAILED" };
};
